/**
 * Custom Hook für Firebase Functions
 * 
 * Verwaltet die Initialisierung und den Aufruf von Firebase Cloud Functions
 */

import { useMemo } from 'react';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { functions, auth, app } from '../firebase';

/**
 * Custom Hook für Firebase Functions
 */
export function useFirebaseFunctions() {
  // Initialisiere Functions nur einmal - stelle sicher, dass die gleiche App verwendet wird
  const functionsInstance = useMemo(() => {
    try {
      // Verwende die bereits initialisierte Functions-Instanz oder erstelle eine neue mit der App
      if (functions) {
        return functions;
      }
      // Falls functions nicht verfügbar ist, erstelle eine neue mit der App
      // WICHTIG: Region muss mit der deployed Function übereinstimmen (europe-west6)
      if (app) {
        return getFunctions(app, 'europe-west6');
      }
      return getFunctions();
    } catch (error) {
      console.error('Fehler beim Initialisieren von Firebase Functions:', error);
      return null;
    }
  }, []);

  /**
   * Ruft die translateEmail Function auf
   */
  const translateEmail = useMemo(() => {
    if (!functionsInstance) return null;
    try {
      // Erstelle die callable Function - sie verwendet automatisch die Auth der App
      return httpsCallable(functionsInstance, 'translateEmail');
    } catch (error) {
      console.error('Fehler beim Erstellen der translateEmail Function:', error);
      return null;
    }
  }, [functionsInstance]);

  /**
   * Wrapper für translateEmail mit Fehlerbehandlung
   */
  const callTranslateEmail = async (data, currentUser) => {
    if (!translateEmail) {
      throw new Error('Firebase Functions sind nicht verfügbar.');
    }

    // Prüfe, ob Benutzer authentifiziert ist
    const user = currentUser || auth.currentUser;
    if (!user) {
      throw new Error('Du musst angemeldet sein, um diese Funktion zu verwenden. Bitte melde dich an.');
    }

    try {
      // WICHTIG: Prüfe, ob der Token vom richtigen Projekt stammt
      // Das verhindert Fehler, wenn der User im falschen Projekt eingeloggt ist
      console.log('🔍 Prüfe Auth-Status für User:', user.uid);
      console.log('🔍 auth.currentUser:', auth.currentUser ? auth.currentUser.uid : 'null');
      
      // Stelle sicher, dass auth.currentUser gesetzt ist
      if (auth.currentUser?.uid !== user.uid) {
        console.warn('⚠️ auth.currentUser stimmt nicht überein. Warte auf Sync...');
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      // Aktualisiere den Token explizit - das stellt sicher, dass er gültig ist
      console.log('🔄 Aktualisiere Auth-Token für User:', user.uid);
      const token = await user.getIdToken(true).catch(err => {
        console.error('❌ Fehler beim Aktualisieren des Auth-Tokens:', err);
        throw new Error('Authentifizierungsfehler: Token konnte nicht aktualisiert werden. Bitte melde dich ab und wieder an.');
      });
      
      // WICHTIG: Validiere, dass der Token vom richtigen Projekt stammt (curiculumner)
      const expectedProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'curiculumner';
      let tokenProjectId = null;
      
      if (token) {
        try {
          // Dekodiere den Token (nur Header und Payload, keine Verifizierung)
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            tokenProjectId = payload.aud;
            
            console.log('🔍 Token Project ID (aud):', tokenProjectId);
            console.log('🔍 Erwartete Project ID:', expectedProjectId);
            
            if (tokenProjectId && tokenProjectId !== expectedProjectId) {
              console.error('❌ FALSCHES PROJEKT ERKANNT!');
              console.error(`Token stammt von "${tokenProjectId}", erwartet wird "${expectedProjectId}"`);
              // WICHTIG: Wirf den Fehler BEVOR die Function aufgerufen wird
              throw new Error(
                `❌ Du bist im falschen Firebase-Projekt eingeloggt!\n\n` +
                `Erwartetes Projekt: ${expectedProjectId}\n` +
                `Aktuelles Projekt: ${tokenProjectId}\n\n` +
                `Bitte melde dich ab (Logout-Button oben rechts) und wieder an, um im richtigen Projekt eingeloggt zu sein.`
              );
            }
          }
        } catch (tokenError) {
          // Wenn es ein Projekt-Mismatch-Fehler ist, werfe ihn weiter (verhindert Function-Aufruf)
          if (tokenError.message && tokenError.message.includes('falschen Firebase-Projekt')) {
            throw tokenError; // WICHTIG: Wirft den Fehler, verhindert Function-Aufruf
          }
          // Andere Token-Dekodierungs-Fehler sind nicht kritisch
          console.warn('⚠️ Konnte Token nicht vollständig dekodieren:', tokenError.message);
        }
      }
      
      // ZUSÄTZLICHE PRÜFUNG: Falls tokenProjectId gesetzt ist und nicht übereinstimmt, verhindere Function-Aufruf
      if (tokenProjectId && tokenProjectId !== expectedProjectId) {
        const errorMessage = `❌ Du bist im falschen Firebase-Projekt eingeloggt!\n\n` +
          `Erwartetes Projekt: ${expectedProjectId}\n` +
          `Aktuelles Projekt: ${tokenProjectId}\n\n` +
          `Bitte melde dich ab (Logout-Button oben rechts) und wieder an, um im richtigen Projekt eingeloggt zu sein.`;
        console.error(errorMessage);
        throw new Error(errorMessage);
      }
      
      console.log('✅ Auth-Token validiert, Länge:', token ? token.length : 0);
      console.log('✅ auth.currentUser:', auth.currentUser ? auth.currentUser.uid : 'null');
      console.log('🔄 Rufe translateEmail Function auf...');
      
      // Rufe die Function auf - httpsCallable sollte automatisch den Token mitsenden
      // Bei v2 Functions wird der Token automatisch aus auth.currentUser geholt
      const result = await translateEmail(data);
      console.log('✅ translateEmail Function erfolgreich aufgerufen');
      return result;
    } catch (error) {
      console.error('translateEmail Function Error:', error);
      
      // Spezifische Fehlerbehandlung
      if (error.code === 'unauthenticated' || error.code === 'permission-denied') {
        // Prüfe, ob es ein Auth-Problem ist
        if (error.message && error.message.includes('User must be authenticated')) {
          throw new Error('Du musst angemeldet sein, um diese Funktion zu verwenden. Bitte melde dich an und versuche es erneut.');
        }
        throw new Error('Authentifizierungsfehler: Bitte melde dich ab und wieder an, dann versuche es erneut.');
      } else if (error.code === 'resource-exhausted') {
        throw new Error('Zu viele Anfragen. Bitte versuche es später erneut.');
      } else if (error.code === 'failed-precondition') {
        throw new Error('OpenAI API Key ist nicht konfiguriert. Bitte setze den API Key in Firebase Functions.');
      } else if (error.code === 'unavailable') {
        throw new Error('Firebase Functions sind derzeit nicht verfügbar. Bitte versuche es später erneut.');
      }
      
      throw error;
    }
  };

  return {
    functions: functionsInstance,
    translateEmail: callTranslateEmail,
    isAvailable: !!functionsInstance && !!translateEmail,
  };
}
